import { useEffect, useState, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import * as api from '@/lib/api'
import type { SearchResult } from '@/lib/api'

const archiveColors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  treaty: { bg: 'bg-blue-900/20', border: 'border-blue-500/30', text: 'text-blue-300', badge: 'bg-blue-500/20 text-blue-300' },
  conflict: { bg: 'bg-red-900/20', border: 'border-red-500/30', text: 'text-red-300', badge: 'bg-red-500/20 text-red-300' },
  civilization: { bg: 'bg-amber-900/20', border: 'border-amber-500/30', text: 'text-amber-300', badge: 'bg-amber-500/20 text-amber-300' },
  origin: { bg: 'bg-emerald-900/20', border: 'border-emerald-500/30', text: 'text-emerald-300', badge: 'bg-emerald-500/20 text-emerald-300' },
  lineage: { bg: 'bg-purple-900/20', border: 'border-purple-500/30', text: 'text-purple-300', badge: 'bg-purple-500/20 text-purple-300' },
  classified: { bg: 'bg-slate-900/20', border: 'border-slate-500/30', text: 'text-slate-300', badge: 'bg-slate-500/20 text-slate-300' },
  exploration: { bg: 'bg-cyan-900/20', border: 'border-cyan-500/30', text: 'text-cyan-300', badge: 'bg-cyan-500/20 text-cyan-300' },
  mythology: { bg: 'bg-indigo-900/20', border: 'border-indigo-500/30', text: 'text-indigo-300', badge: 'bg-indigo-500/20 text-indigo-300' },
}

const archiveIcons: Record<string, string> = {
  treaty: '📜',
  conflict: '⚔️',
  civilization: '🏛️',
  origin: '🏢',
  lineage: '👑',
  classified: '🔐',
  exploration: '🧭',
  mythology: '⚡',
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialQuery = searchParams.get('q') || ''

  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [total, setTotal] = useState(0)

  useSEO({
    title: initialQuery ? `Search: ${initialQuery}` : 'Search Archives',
    description: 'Search across our growing network of specialized archives for historical entries, figures, and events.',
    url: 'https://thearchivenetwork.com/search',
  })

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([])
      setTotal(0)
      setSearched(false)
      return
    }

    setLoading(true)
    setSearched(true)

    try {
      const response = await api.search(searchQuery)
      setResults(response.results || [])
      setTotal(response.total || 0)
    } catch (error) {
      console.error('Search failed:', error)
      setResults([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [])

  // Search on initial load if query params exist
  useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchParams({ q: query })
    performSearch(query)
  }

  // Group results by archive
  const resultsByArchive = results.reduce((acc, result) => {
    if (!acc[result.archive_key]) {
      acc[result.archive_key] = []
    }
    acc[result.archive_key].push(result)
    return acc
  }, {} as Record<string, SearchResult[]>)

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl sm:text-5xl text-archive-cream">
            Search Archives
          </h1>
          <p className="mt-4 text-lg text-archive-muted max-w-2xl mx-auto">
            Search across all archives in the network for historical entries, figures, and events.
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for myths, conflicts, civilizations, treaties..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-6 py-4 pl-14 bg-archive-slate border border-archive-stone/50 rounded-xl text-lg text-archive-cream placeholder-archive-muted focus:outline-none focus:border-archive-gold/50 transition-colors"
              autoFocus
            />
            <svg
              className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-archive-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  setResults([])
                  setSearched(false)
                  setSearchParams({})
                }}
                className="absolute right-20 top-1/2 -translate-y-1/2 text-archive-muted hover:text-archive-cream"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-2 bg-archive-gold text-archive-black rounded-lg font-medium hover:bg-archive-amber transition-colors"
            >
              Search
            </button>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : searched ? (
          results.length > 0 ? (
            <div className="space-y-4">
              <p className="text-archive-muted text-sm">
                Found {total} result{total !== 1 ? 's' : ''} for "{searchParams.get('q')}"
              </p>

              {/* Results grouped by archive */}
              {Object.entries(resultsByArchive).map(([archiveKey, archiveResults]) => {
                const colors = archiveColors[archiveKey] || archiveColors.treaty
                return (
                  <div key={archiveKey} className="space-y-3">
                    <h2 className={`font-display text-lg ${colors.text} flex items-center gap-2`}>
                      {archiveIcons[archiveKey]} {archiveResults[0]?.archive_name}
                      <span className="text-sm text-archive-muted font-normal">
                        ({archiveResults.length})
                      </span>
                    </h2>
                    {archiveResults.map((result) => (
                      <a
                        key={`${result.archive_key}-${result.slug}`}
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block p-4 rounded-lg border ${colors.border} ${colors.bg} hover:border-archive-gold/50 transition-all group`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-display text-lg text-archive-cream group-hover:text-archive-gold transition-colors">
                              {result.name}
                            </h3>
                            {result.one_liner && (
                              <p className="mt-1 text-sm text-archive-muted line-clamp-2">
                                {result.one_liner}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-archive-muted group-hover:text-archive-gold transition-colors"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-archive-muted">
                No results found for "{searchParams.get('q')}".
              </p>
              <p className="text-archive-muted mt-2">
                Try a different search term.
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏛️</div>
            <p className="text-archive-muted text-lg">
              Enter a search term to explore across all archives.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm text-archive-muted">
              <span>Try:</span>
              {['Zeus', 'World War II', 'Roman Empire', 'Apple', 'Treaty of Versailles'].map((term) => (
                <button
                  key={term}
                  onClick={() => {
                    setQuery(term)
                    setSearchParams({ q: term })
                    performSearch(term)
                  }}
                  className="text-archive-gold hover:text-archive-amber"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Back link */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-archive-muted hover:text-archive-gold transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to The Archive Network
          </Link>
        </div>
      </div>
    </div>
  )
}
